import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { loadEstimate } from '../estimate.js';
import { money, count } from '../lib/format.js';
import { SIZE_TIERS, sizeTierLabel } from '../lib/size.js';

const OTHER = '__other__';
const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
/** Tip the Scale (Estimate Tool 6.7): half the base at one end, double it at the other. */
const SCALE_MIN = 50;
const SCALE_MAX = 200;
const SCALE_NEUTRAL = 100;
const SCALE_STEP = 5;
/** Long enough that typing a rating does not fire a request per keystroke. */
const PRICE_DEBOUNCE_MS = 250;

/**
 * The estimate editor (Estimate Tool 6; Phase E7) — `/estimate/new` and `/estimate/:id`.
 *
 * A route rather than a modal, because an estimate is the thing this tool exists to produce and
 * a modal would make it unlinkable (2.2). Both addresses reach this same component; the only
 * difference is whether it was handed a record.
 *
 * Four things the design is particular about, and they are the reason this file is the size it
 * is:
 *
 * **The ratings are the client's, and the override is the estimate's.** Choosing a rated company
 * fills both fields and says where they came from. Editing one marks it overridden and shows
 * both numbers, so the estimator can see what they departed from. The override is stored on the
 * estimate and **never written back to Workamajig** — the estimator is saying something about
 * this engagement, not correcting the client record. Correcting the client is Data Hygiene, and
 * the screen links there.
 *
 * **An unrated company cannot be priced.** The fields open empty and the screen links to that
 * client on Data Hygiene rather than inviting a guess.
 *
 * **"Other" creates nothing.** It records a typed name and `is_new_company`; no company reaches
 * Workamajig or the directory (6.1, and open question 12.3).
 *
 * **A saved estimate renders its stored result.** Opening one shows the number it was saved
 * with, whatever has happened to the history or the settings since (4.3). When a recompute
 * would now differ the screen says so, with both numbers — it does not quietly show the new one,
 * and it does not hide that the world moved.
 */
class EstimateEditor extends StoreMixin(LitElement) {
  static properties = {
    route: { type: Object },
    _estimate: { state: true },
    _form: { state: true },
    _price: { state: true },
    _current: { state: true },
    _pricing: { state: true },
    _saving: { state: true },
    _error: { state: true },
    _loadError: { state: true },
    _dirty: { state: true },
  };

  constructor() {
    super();
    this.route = null;
    this._estimate = null;
    this._form = null;
    this._price = null; // what the inputs on screen price at
    this._current = null; // what the saved estimate would price at today
    this._pricing = false;
    this._saving = false;
    this._error = null;
    this._loadError = null;
    this._dirty = false;
    this._timer = null;
  }

  static styles = css`
    :host {
      display: block;
    }
    .grid {
      display: grid;
      gap: 18px;
      grid-template-columns: minmax(0, 420px) minmax(0, 1fr);
      align-items: start;
    }
    @media (max-width: 900px) {
      .grid {
        grid-template-columns: minmax(0, 1fr);
      }
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 14px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    .field {
      display: grid;
      gap: 5px;
      margin-bottom: 15px;
    }
    label {
      font-size: 13px;
      font-weight: 700;
      color: var(--ink-strong);
    }
    input,
    select {
      font: inherit;
      font-size: 14px;
      padding: 7px 9px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--control-bg);
      color: var(--ink-strong);
      width: 100%;
      box-sizing: border-box;
    }
    .pair {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    .pct {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .pct input {
      width: 8ch;
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    .hint {
      font-size: 12px;
      color: var(--ink-muted);
      line-height: 1.45;
    }
    .link {
      font: inherit;
      font-size: inherit;
      padding: 0;
      border: 0;
      background: none;
      color: inherit;
      text-decoration: underline;
      cursor: pointer;
    }
    /* Tip the Scale: the two ends named, the number beside the handle. */
    .scale {
      padding: 12px 0 2px;
      border-top: 1px solid var(--border-soft);
      border-bottom: 1px solid var(--border-soft);
      margin-bottom: 15px;
    }
    .scale-row {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .scale-row input[type='range'] {
      flex: 1;
      width: auto;
      padding: 0;
      background: none;
      border: 0;
      accent-color: var(--accent);
    }
    .end {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-muted);
      white-space: nowrap;
    }
    .value {
      min-width: 5ch;
      text-align: right;
      font-variant-numeric: tabular-nums;
      font-weight: 700;
      font-size: 14px;
      color: var(--ink-muted);
    }
    .value.tipped {
      color: var(--ink-strong);
    }
    .hint a {
      color: inherit;
    }
    /* A rating that departs from the client's: both numbers, so the departure is visible. */
    .overridden {
      color: var(--status-caution, var(--ink));
      font-weight: 600;
    }
    .source {
      font-size: 12px;
      color: var(--ink-muted);
    }
    .warn {
      margin: 0 0 14px;
      padding: 9px 12px;
      border: 1px solid var(--border);
      border-left: 3px solid var(--status-caution, var(--ink-muted));
      border-radius: 6px;
      background: var(--surface-2);
      font-size: 13px;
      line-height: 1.5;
    }
    .warn a {
      color: inherit;
    }
    .actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 16px;
      padding-top: 14px;
      border-top: 1px solid var(--border-soft);
    }
    button {
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      padding: 8px 16px;
      border: 1px solid var(--control-border);
      border-radius: 6px;
      background: var(--surface);
      color: var(--ink-strong);
      cursor: pointer;
    }
    button.primary {
      background: var(--ink-strong);
      color: var(--surface);
      border-color: var(--ink-strong);
    }
    button[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
      font-size: 13px;
    }
    .state {
      color: var(--ink-muted);
      padding: 16px 0;
    }
    .stale {
      margin: 0 0 14px;
      padding: 9px 12px;
      border: 1px solid var(--status-caution, var(--border));
      border-left: 3px solid var(--status-caution, var(--ink-muted));
      border-radius: 6px;
      background: var(--surface-2);
      font-size: 13px;
      line-height: 1.5;
    }
    .stale strong {
      font-variant-numeric: tabular-nums;
    }
    /* Create Schedule (Estimate Tool 6.5): the other half of the estimator's job, and the reason
       it is full-width and primary rather than a link in the corner. The price answers what to
       charge; the schedule answers what to do. */
    .schedule {
      margin-top: 14px;
      padding-top: 14px;
      border-top: 1px solid var(--border-soft);
    }
    .schedule button {
      width: 100%;
      justify-content: center;
    }
    .schedule .hint {
      margin: 7px 0 0;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    loadEstimate();
    this._boot();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._timer);
  }

  updated(changed) {
    if (changed.has('route') && this._bootedFor !== this._id) this._boot();
  }

  get _id() {
    return this.route?.params?.id ?? null;
  }

  get _companies() {
    return this.appState.estimate.data?.companies ?? [];
  }

  get _settings() {
    return this.appState.estimate.data?.settings ?? {};
  }

  /** The five rungs and what each template holds (8.4), from the bootstrap. */
  get _ladder() {
    return this.appState.estimate.data?.ladder ?? [];
  }

  /** The rate sheets a budget can be converted through (8.11), from the bootstrap. */
  get _rateSheets() {
    return this.appState.estimate.data?.rate_sheets ?? [];
  }

  async _boot() {
    this._bootedFor = this._id;
    if (!this._id) {
      this._estimate = null;
      this._form = {
        company_id: null,
        company_name: '',
        is_new_company: false,
        project_name: '',
        org_complexity: '',
        human_factor: '',
        size_tier: '',
        delivery_date: '',
        budget_amount: '',
        rate_sheet_id: '',
        value_add_pct: 0,
        principal_investment_pct: 0,
        scale_pct: SCALE_NEUTRAL,
      };
      this._dirty = false;
      return;
    }
    try {
      const { estimate } = await api.get(`/estimate/estimates/${this._id}`);
      this._estimate = estimate;
      this._form = {
        company_id: estimate.company_id,
        company_name: estimate.company_name,
        is_new_company: estimate.is_new_company,
        project_name: estimate.project_name,
        org_complexity: estimate.org_complexity,
        human_factor: estimate.human_factor,
        size_tier: estimate.size_tier ?? '',
        delivery_date: estimate.delivery_date ?? '',
        budget_amount: estimate.budget_amount ?? '',
        rate_sheet_id: estimate.rate_sheet_id ?? '',
        value_add_pct: estimate.value_add_pct,
        principal_investment_pct: estimate.principal_investment_pct,
        scale_pct: estimate.scale_pct ?? SCALE_NEUTRAL,
      };
      this._price = null;
      this._dirty = false;
      this._loadError = null;
      // What the same inputs would price at today, so the screen can say the world moved.
      this._checkDrift();
    } catch (err) {
      this._loadError = `Could not open this estimate: ${err.message}`;
    }
  }

  /** The client whose ratings this estimate started from, if it has one. */
  get _company() {
    return this._companies.find((c) => c.id === this._form?.company_id) ?? null;
  }

  _overridden(field) {
    const c = this._company;
    if (!c || c[field] === null || this._form[field] === '') return false;
    return Number(this._form[field]) !== Number(c[field]);
  }

  _set(key, value) {
    this._form = { ...this._form, [key]: value };
    this._dirty = true;
    this._error = null;
    this._schedulePrice();
  }

  /**
   * Choosing a company fills both ratings from the client and names the source. An unrated one
   * opens the fields empty rather than inviting a guess; "Other" records a name and creates
   * nothing.
   */
  _pickCompany(value) {
    if (value === OTHER) {
      this._form = { ...this._form, company_id: null, is_new_company: true, company_name: '' };
      this._dirty = true;
      this._schedulePrice();
      return;
    }
    const c = this._companies.find((x) => x.id === value);
    this._form = {
      ...this._form,
      company_id: c?.id ?? null,
      company_name: c?.name ?? '',
      is_new_company: false,
      org_complexity: c?.org_complexity ?? '',
      human_factor: c?.human_factor ?? '',
    };
    this._dirty = true;
    this._error = null;
    this._schedulePrice();
  }

  _schedulePrice() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this._price_(), PRICE_DEBOUNCE_MS);
  }

  /** Price what is on screen. Named with a trailing underscore to leave `_price` the state. */
  async _price_() {
    const { org_complexity, human_factor, size_tier } = this._form;
    if (!RATINGS.includes(Number(org_complexity)) || !RATINGS.includes(Number(human_factor))) {
      this._price = null;
      return;
    }
    this._pricing = true;
    try {
      this._price = await api.post('/estimate/price', {
        org_complexity: Number(org_complexity),
        human_factor: Number(human_factor),
        // The third axis (Estimate Tool 7.2). Empty is a real answer — no size chosen, so the
        // axis does not score — and is sent as null rather than omitted, so the payload says
        // what was asked rather than leaving the server to infer it.
        size_tier: size_tier || null,
      });
    } catch (err) {
      this._error = `Could not price this: ${err.message}`;
    } finally {
      this._pricing = false;
    }
  }

  /** What the saved estimate's own inputs would price at today (Estimate Tool 4.3). */
  async _checkDrift() {
    const e = this._estimate;
    if (!e?.result) return;
    try {
      this._current = await api.post('/estimate/price', {
        org_complexity: e.org_complexity,
        human_factor: e.human_factor,
        size_tier: e.size_tier ?? null,
      });
    } catch {
      this._current = null;
    }
  }

  get _adjustments() {
    const base = this._shownPrice?.base;
    if (base === null || base === undefined) return null;
    const va = Number(this._form.value_add_pct) || 0;
    const pi = Number(this._form.principal_investment_pct) || 0;
    const scale = Number(this._form.scale_pct) || SCALE_NEUTRAL;
    // Tip first, then add, then discount (6.7, 7.5) — not a single net adjustment.
    const scaled = base * (scale / 100);
    const withValue = scaled * (1 + va / 100);
    const total = withValue * (1 - pi / 100);
    return {
      scale_pct: scale,
      scale_amount: scale === SCALE_NEUTRAL ? null : Math.round(scaled - base),
      scaled_base: Math.round(scaled),
      value_add_pct: va,
      principal_investment_pct: pi,
      value_add_amount: Math.round(withValue - scaled),
      principal_investment_amount: Math.round(total - withValue),
      total: Math.round(total),
    };
  }

  /**
   * A saved estimate shows its **stored** result until an input is touched; from then on it
   * shows what the inputs on screen price at, because that is what saving would store.
   */
  get _shownPrice() {
    if (!this._dirty && this._estimate?.result) {
      const r = this._estimate.result;
      return {
        method_version: r.method_version,
        base: r.base_amount,
        band_low: r.band_low,
        band_high: r.band_high,
        mean: r.mean_amount,
        analog_count: r.analog_count,
        pool_size: r.pool_size,
        above_floor: r.analog_count,
        floor_lifted: r.floor_lifted,
        effective_floor: r.effective_floor,
        settings_snapshot: r.settings_snapshot,
        analogs: r.analogs,
      };
    }
    return this._price;
  }

  get _driftedBase() {
    if (this._dirty || !this._estimate?.result || !this._current) return null;
    const stored = this._estimate.result.base_amount;
    return this._current.base !== stored ? this._current.base : null;
  }

  get _canSave() {
    const f = this._form;
    return Boolean(
      f &&
      String(f.company_name).trim() &&
      String(f.project_name).trim() &&
      RATINGS.includes(Number(f.org_complexity)) &&
      RATINGS.includes(Number(f.human_factor)) &&
      !this._saving,
    );
  }

  async _save() {
    this._saving = true;
    this._error = null;
    const f = this._form;
    const body = {
      company_id: f.is_new_company ? null : f.company_id,
      company_name: String(f.company_name).trim(),
      is_new_company: Boolean(f.is_new_company),
      project_name: String(f.project_name).trim(),
      org_complexity: Number(f.org_complexity),
      human_factor: Number(f.human_factor),
      size_tier: f.size_tier || null,
      delivery_date: f.delivery_date || null,
      // A set budget (6.10): dollars or nothing. The sheet is sent only when one was chosen;
      // empty means the default, which the server resolves so the estimate never pins a sheet
      // nobody picked.
      budget_amount:
        f.budget_amount === '' || f.budget_amount === null ? null : Number(f.budget_amount),
      rate_sheet_id: f.rate_sheet_id || null,
      value_add_pct: Number(f.value_add_pct) || 0,
      principal_investment_pct: Number(f.principal_investment_pct) || 0,
      scale_pct: Number(f.scale_pct) || SCALE_NEUTRAL,
    };
    try {
      if (this._id) {
        const { estimate } = await api.patch(`/estimate/estimates/${this._id}`, body);
        this._estimate = estimate;
        this._dirty = false;
        this._price = null;
        this._current = null;
        this._checkDrift();
      } else {
        const { estimate } = await api.post('/estimate/estimates', body);
        navigate(`/estimate/${estimate.id}`);
      }
    } catch (err) {
      this._error = err.message;
    } finally {
      this._saving = false;
    }
  }

  _companyField() {
    const f = this._form;
    const companies = this._companies;
    // Active clients first, Other last (Estimate Tool 6.1).
    const active = companies.filter((c) => c.is_active_client);
    const rest = companies.filter((c) => !c.is_active_client);

    if (f.is_new_company) {
      return html`
        <div class="field">
          <label for="company">Company</label>
          <input
            id="company"
            .value=${f.company_name}
            placeholder="Name of the company"
            @input=${(e) => this._set('company_name', e.target.value)}
          />
          <p class="hint">
            Recorded on this estimate only — nothing is created in Workamajig.
            <button class="link" @click=${() => this._pickCompany('')}>
              Pick a client instead
            </button>
          </p>
        </div>
      `;
    }

    const option = (c) => html`
      <option value=${c.id} ?selected=${c.id === f.company_id}>
        ${c.name}${c.org_complexity === null || c.human_factor === null ? ' — unrated' : ''}
      </option>
    `;

    return html`
      <div class="field">
        <label for="company">Company</label>
        <select id="company" @change=${(e) => this._pickCompany(e.target.value)}>
          <option value="" ?selected=${!f.company_id}>Choose a client…</option>
          ${active.map(option)}${rest.map(option)}
          <option value=${OTHER}>Other — not a Workamajig client</option>
        </select>
      </div>
    `;
  }

  _ratingField(field, label) {
    const f = this._form;
    const c = this._company;
    const overridden = this._overridden(field);
    return html`
      <div class="field">
        <label for=${field}>${label}</label>
        <select id=${field} @change=${(e) => this._set(field, e.target.value)}>
          <option value="" ?selected=${f[field] === '' || f[field] === null}>—</option>
          ${RATINGS.map(
            (n) => html`<option value=${n} ?selected=${Number(f[field]) === n}>${n}</option>`,
          )}
        </select>
        ${
          overridden
            ? html`<span class="source overridden"
                >Overridden for this estimate — ${c.name} is ${c[field]} in Workamajig. The client
                is not changed.</span
              >`
            : c && c[field] !== null
              ? html`<span class="source">From ${c.name} in Workamajig.</span>`
              : nothing
        }
      </div>
    `;
  }

  /**
   * The size analog (Estimate Tool 6.8) — the third matching axis, and the estimate's answer to
   * the one question the two ratings cannot ask.
   *
   * It lives here rather than on the Create Schedule screen, where it started life as that
   * screen's "Size" selector. Two reasons, and the second is the one that mattered. It is a
   * *pricing* input: the pool is scored on it, so it has to be chosen before there is a price to
   * look at, and a control that moves the number belongs beside the number it moves. And an
   * estimate and its schedule disagreeing about how big the job is was a contradiction waiting
   * to be found — one answer, on the document that is sent to the client, feeding both halves.
   *
   * Left empty it is not a default: the axis simply does not score, and the two ratings share
   * the whole between them exactly as they did before this existed.
   */
  _sizeField() {
    const f = this._form;
    const ladder = this._ladder ?? [];
    // The rung's own words for its size: a stated range where it has one (Landing Page is
    // 4-16 h), its template's total otherwise. Never the raw number for a rung that states a
    // range — "about 8 h" would be this tool's arithmetic showing through where a person gave
    // it a straight answer.
    const sizeOf = (tier) => ladder.find((t) => t.tier === tier)?.label ?? null;
    const chosen = f.size_tier || '';
    return html`
      <div class="field">
        <label for="size_tier">Size</label>
        <select id="size_tier" @change=${(e) => this._set('size_tier', e.target.value)}>
          <option value="" ?selected=${!chosen}>Not sized — match on the ratings alone</option>
          ${SIZE_TIERS.map((tier) => {
            const size = sizeOf(tier);
            return html`<option value=${tier} ?selected=${chosen === tier}>
              ${sizeTierLabel(tier)}${size ? ` — ${size}` : ''}
            </option>`;
          })}
        </select>
        <span class="hint">
          ${
            chosen
              ? html`Comparable projects of about this size weigh more. It is also the template the
                schedule cuts from.`
              : html`The two ratings say how hard the client is, not how big the site is. Choosing a
                size narrows the comparables to projects of about this scale.`
          }
        </span>
      </div>
    `;
  }

  /**
   * The delivery commitment (Estimate Tool 8.6).
   *
   * Not a pricing input, and the hint says so: nothing in Section 7 prices a date. It is here
   * because the commitment is made when the work is quoted, and because the Create Schedule
   * screen should not have to ask again for something the estimate already knows.
   */
  _deliveryField() {
    return html`
      <div class="field">
        <label for="delivery_date">Delivery (optional)</label>
        <input
          id="delivery_date"
          type="date"
          .value=${this._form.delivery_date ?? ''}
          @input=${(e) => this._set('delivery_date', e.target.value)}
        />
        <span class="hint">
          The commitment. It does not move the price; the schedule is built backwards from it.
        </span>
      </div>
    `;
  }

  /**
   * A set budget (Estimate Tool 6.10; 2026-09-23).
   *
   * What the client will pay, when that is known before the work is scheduled. It is the third
   * way a schedule gets its size: the budget is turned into hours through the rate sheet beside
   * it and the plan is scaled to hit it to the dollar (8.11). Like the delivery date it is not a
   * pricing input — the analog price beside it is what the work is worth, the budget is what is
   * on the table, and the screen shows both rather than letting one overwrite the other.
   */
  _budgetField() {
    const f = this._form;
    const base =
      this._estimate?.result?.total_amount ?? this._estimate?.result?.base_amount ?? null;
    const budget =
      f.budget_amount === '' || f.budget_amount === null ? null : Number(f.budget_amount);
    const gap = budget !== null && base !== null ? budget - base : null;
    return html`
      <div class="field">
        <label for="budget_amount">Budget (optional)</label>
        <div class="pct">
          <span class="unit">$</span>
          <input
            id="budget_amount"
            type="number"
            min="0"
            step="1"
            placeholder="Not set"
            .value=${f.budget_amount === null || f.budget_amount === undefined ? '' : String(f.budget_amount)}
            @input=${(e) => this._set('budget_amount', e.target.value)}
          />
        </div>
        <span class="hint">
          ${
            budget !== null
              ? html`The schedule is scaled to come to this amount at the rate sheet's
                rates.${
                  gap !== null
                    ? html` That is
                        <strong>${money(Math.abs(gap))}</strong> ${gap >= 0 ? 'above' : 'below'} the
                        analog price.`
                    : nothing
                }`
              : 'A set budget. It does not move the price; the schedule is scaled to hit it.'
          }
        </span>
      </div>
    `;
  }

  /** The sheet a budget is converted through (8.11). Empty is the default sheet, named. */
  _rateSheetField() {
    const f = this._form;
    const sheets = this._rateSheets;
    const fallback = sheets.find((r) => r.is_default);
    return html`
      <div class="field">
        <label for="rate_sheet_id">Rate sheet</label>
        <select id="rate_sheet_id" @change=${(e) => this._set('rate_sheet_id', e.target.value)}>
          <option value="" ?selected=${!f.rate_sheet_id}>
            ${fallback ? `${fallback.name} — the default` : 'The default sheet'}
          </option>
          ${sheets
            .filter((r) => !r.is_default)
            .map(
              (r) =>
                html`<option value=${r.id} ?selected=${f.rate_sheet_id === r.id}>
                  ${r.name}
                </option>`,
            )}
        </select>
        <span class="hint">
          One hourly rate per service. The standard sheet is Workamajig's own service rates; others
          are kept on Settings.
        </span>
      </div>
    `;
  }

  _pctField(field, label, max) {
    return html`
      <div class="field">
        <label for=${field}>${label}</label>
        <div class="pct">
          <input
            id=${field}
            type="number"
            min="0"
            max=${max}
            step="1"
            .value=${String(this._form[field] ?? 0)}
            @input=${(e) => this._set(field, e.target.value)}
          />
          <span class="hint">% · up to ${max}</span>
        </div>
      </div>
    `;
  }

  /**
   * Tip the Scale (Estimate Tool 6.7).
   *
   * The analog base is a weighted median of what comparable clients were billed, and Section 7.6
   * is candid that the two rating axes cannot measure how big a website is — the pool's gross
   * runs from $200 to $500,102. So an estimator who can see the band needs a way to say "this
   * one sits high in that range". Without it they would reach for Value Add, which means
   * something specific to a client and should not be made to carry this.
   *
   * The ends are named for who the tip favours rather than for the arithmetic: toward the
   * Client is cheaper, toward BE is dearer. The number and its effect on the money are both on
   * screen, because a slider whose only feedback is its own position is a guess.
   */
  _scaleField() {
    const value = Number(this._form.scale_pct ?? SCALE_NEUTRAL);
    const neutral = value === SCALE_NEUTRAL;
    const toward = value < SCALE_NEUTRAL ? 'the client' : 'BE';
    return html`
      <div class="field scale">
        <label for="scale_pct">Tip the Scale</label>
        <div class="scale-row">
          <span class="end">Client</span>
          <input
            id="scale_pct"
            type="range"
            min=${SCALE_MIN}
            max=${SCALE_MAX}
            step=${SCALE_STEP}
            .value=${String(value)}
            aria-valuetext=${
              neutral
                ? 'Neutral, 100 per cent of the comparable base'
                : `${value} per cent of the comparable base, toward ${toward}`
            }
            @input=${(e) => this._set('scale_pct', Number(e.target.value))}
          />
          <span class="end">BE</span>
          <output for="scale_pct" class="value ${neutral ? '' : 'tipped'}">${value}%</output>
        </div>
        <p class="hint">
          ${
            neutral
              ? html`The comparable base, untouched. Slide toward <strong>Client</strong> to come
                  down as far as half, or toward <strong>BE</strong> to go up as far as double.`
              : html`<strong>${value}%</strong> of the comparable base — tipped toward ${toward}.
                  <button class="link" @click=${() => this._set('scale_pct', SCALE_NEUTRAL)}>
                    Back to neutral</button
                  >.`
          }
        </p>
      </div>
    `;
  }

  /**
   * Create Schedule (Estimate Tool 6.5, 8.1).
   *
   * Enabled once the estimate has a company and a saved result, because there is no target to
   * scale a schedule to before then: the tier is recommended from the analog set the price was
   * computed against, and an unsaved estimate has none. It says which project it last scheduled,
   * if any, which is how an estimator coming back to a quote from three weeks ago finds the draft
   * they left rather than generating a second one.
   */
  _scheduleAction() {
    if (!this._id) return nothing;
    const e = this._estimate;
    const ready = Boolean(e?.result && String(e?.company_name ?? '').trim());
    const last = e?.schedule ?? null;
    return html`
      <div class="schedule">
        <button
          class="primary"
          ?disabled=${!ready}
          @click=${() => navigate(`/estimate/${this._id}/schedule`)}
        >
          Create Schedule
        </button>
        <p class="hint">
          ${
            !ready
              ? 'Save this estimate with a company and a price first — a schedule is scaled to the hours the analogs argued for.'
              : last
                ? html`Last scheduled
                  ${
                    last.target_project_number
                      ? html`into <strong>${last.target_project_number}</strong>`
                      : 'with no project chosen'
                  }
                  — ${last.status === 'draft' ? 'still a draft' : last.status}.`
                : 'Groups, tasks and hours by service, into a project that already exists in Workamajig.'
          }
        </p>
      </div>
    `;
  }

  render() {
    if (this._loadError)
      return html`<div class="card"><p class="error">${this._loadError}</p></div>`;
    if (!this._form) return html`<div class="card"><p class="state">Loading…</p></div>`;

    const f = this._form;
    const c = this._company;
    const s = this._settings;
    const unrated = c && (c.org_complexity === null || c.human_factor === null);
    const drifted = this._driftedBase;

    return html`
      <div class="grid">
        <div class="card">
          <div class="eyebrow">${this._id ? 'Estimate' : 'New estimate'}</div>
          <h2>${f.project_name || 'What is this worth?'}</h2>

          ${this._companyField()}
          ${
            unrated
              ? html`<p class="warn">
                  <strong>${c.name} has no ratings in Workamajig</strong>, so nothing can be matched
                  against. Rate them on
                  <a
                    href="/estimate/hygiene"
                    @click=${(e) => {
                      e.preventDefault();
                      navigate('/estimate/hygiene');
                    }}
                    >Data Hygiene</a
                  >
                  and come back.
                </p>`
              : nothing
          }

          <div class="field">
            <label for="project">Project</label>
            <input
              id="project"
              .value=${f.project_name}
              placeholder="What is being quoted"
              @input=${(e) => this._set('project_name', e.target.value)}
            />
          </div>

          <div class="pair">
            ${this._ratingField('org_complexity', 'Organizational complexity')}
            ${this._ratingField('human_factor', 'Human factor')}
          </div>

          <div class="pair">${this._sizeField()} ${this._deliveryField()}</div>

          <div class="pair">${this._budgetField()} ${this._rateSheetField()}</div>

          ${this._scaleField()}

          <div class="pair">
            ${this._pctField('value_add_pct', 'Value Add', s.max_value_add ?? 50)}
            ${this._pctField(
              'principal_investment_pct',
              'Principal Investment',
              s.max_principal_investment ?? 15,
            )}
          </div>

          <div class="actions">
            <button class="primary" ?disabled=${!this._canSave} @click=${() => this._save()}>
              ${this._saving ? 'Saving…' : this._id ? 'Save' : 'Create estimate'}
            </button>
            <button @click=${() => navigate('/estimate')}>Back to estimates</button>
            ${this._error ? html`<span class="error">${this._error}</span>` : nothing}
          </div>

          ${this._scheduleAction()}
        </div>

        <div class="card">
          ${
            drifted !== null
              ? html`<p class="stale">
                  This estimate reads as it was saved: a base of
                  <strong>${money(this._estimate.result.base_amount)}</strong>. Priced again today
                  it would be <strong>${money(drifted)}</strong> — history has grown, a rating has
                  changed, or the settings have moved since. Saving any edit will take the new
                  number.
                </p>`
              : nothing
          }
          <estimate-analogs
            .price=${this._shownPrice}
            .adjustments=${this._adjustments}
            .loading=${this._pricing}
          ></estimate-analogs>
          ${
            !this._shownPrice && !this._pricing
              ? html`<p class="state">
                  Choose a rated client, or set both ratings, and the comparable projects will
                  appear here.
                  ${
                    this.appState.estimate.data
                      ? html`${count(this._companies.filter((x) => x.org_complexity !== null).length)}
                        of ${count(this._companies.length)} clients are rated.`
                      : nothing
                  }
                </p>`
              : nothing
          }
        </div>
      </div>
    `;
  }
}

customElements.define('estimate-editor', EstimateEditor);
